/**
 * Personel İzin Kartı — özet bakiye + talep butonu.
 */

import { useEffect, useState } from 'react'
import { Calendar, Plus, ChevronRight, Clock, AlertCircle } from 'lucide-react'
import {
  personelIzinOzeti,
  type IzinOzeti,
} from '@/lib/services/izin-bakiye'
import { IzinTalepModal } from './IzinTalepModal'
import { IzinlerimModal } from './IzinlerimModal'
import type { KullaniciPersonel } from '@/types'

interface Props {
  personel: KullaniciPersonel
}

export function IzinKarti({ personel }: Props) {
  const [ozet, setOzet] = useState<IzinOzeti | null>(null)
  const [yukleniyor, setYukleniyor] = useState(true)
  const [talepAcik, setTalepAcik] = useState(false)
  const [listeAcik, setListeAcik] = useState(false)

  async function yukle() {
    setYukleniyor(true)
    try {
      const o = await personelIzinOzeti(
        personel.id,
        personel.iseBaslamaTarihi ?? null,
        personel.calismaTipi === 'fulltime',
        personel.yillikIzinBaslangicBakiye ?? 0
      )
      setOzet(o)
    } catch (err) {
      console.error('İzin özeti yüklenemedi:', err)
    } finally {
      setYukleniyor(false)
    }
  }

  useEffect(() => { yukle() }, [personel.id])  // eslint-disable-line

  return (
    <>
      <div className="card">
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="w-5 h-5 text-purple-600" />
          <h2 className="font-bold text-gray-900">İzinlerim</h2>
        </div>

        {yukleniyor ? (
          <div className="text-center py-3">
            <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
          </div>
        ) : ozet ? (
          <>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {ozet.yillik && (
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-2.5">
                  <p className="text-[10px] uppercase tracking-wide text-purple-700 font-semibold">Yıllık</p>
                  <p className="text-2xl font-bold text-purple-900 leading-tight">
                    {ozet.yillik.kalan}
                    <span className="text-xs font-normal text-purple-600 ml-1">gün</span>
                  </p>
                  <p className="text-[10px] text-purple-600 mt-0.5">
                    {ozet.yillik.kullanilan}/{ozet.yillik.toplamHak} kullanıldı
                  </p>
                </div>
              )}
              <div className={`rounded-xl p-2.5 border ${
                ozet.aylik.kalan < 0
                  ? 'bg-red-50 border-red-200'
                  : 'bg-blue-50 border-blue-200'
              }`}>
                <p className={`text-[10px] uppercase tracking-wide font-semibold ${
                  ozet.aylik.kalan < 0 ? 'text-red-700' : 'text-blue-700'
                }`}>Bu Ay</p>
                <p className={`text-2xl font-bold leading-tight ${
                  ozet.aylik.kalan < 0 ? 'text-red-900' : 'text-blue-900'
                }`}>
                  {ozet.aylik.kalan}
                  <span className="text-xs font-normal ml-1">
                    {ozet.aylik.kalan < 0 ? 'gün aşıldı' : 'gün'}
                  </span>
                </p>
                <p className={`text-[10px] mt-0.5 ${
                  ozet.aylik.kalan < 0 ? 'text-red-600' : 'text-blue-600'
                }`}>
                  {ozet.aylik.kullanilan}/{ozet.aylik.hak} kullanıldı
                </p>
              </div>
            </div>

            {ozet.yillik && ozet.yillik.sonrakiKazanimaGun <= 30 && ozet.yillik.sonrakiKazanimaGun >= 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 mb-3 text-xs text-amber-800 flex items-start gap-1.5">
                <Clock className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>{ozet.yillik.sonrakiKazanimaGun} gün</strong> sonra +7 gün yıllık izin kazanacaksın 🎉
                </span>
              </div>
            )}

            {ozet.bekleyenTalep.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 mb-3 text-xs text-yellow-800 flex items-start gap-1.5">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>
                  <strong>{ozet.bekleyenTalep.length}</strong> talep onay bekliyor
                </span>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setTalepAcik(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2.5 rounded-xl flex items-center justify-center gap-1.5 text-sm"
              >
                <Plus className="w-4 h-4" />
                İzin Talebi
              </button>
              <button
                onClick={() => setListeAcik(true)}
                className="bg-white border border-gray-300 text-gray-700 font-semibold py-2.5 rounded-xl flex items-center justify-center gap-1 text-sm hover:bg-gray-50"
              >
                Geçmiş
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </>
        ) : (
          <p className="text-sm text-gray-500">Bilgi alınamadı</p>
        )}
      </div>

      <IzinTalepModal
        acik={talepAcik}
        personel={personel}
        onKapat={() => setTalepAcik(false)}
        onTalepGonderildi={() => {
          setTalepAcik(false)
          yukle()
        }}
      />

      <IzinlerimModal
        acik={listeAcik}
        personel={personel}
        onKapat={() => setListeAcik(false)}
        onDegisiklik={yukle}
      />
    </>
  )
}
